import { useCallback, useState } from 'react';

import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';

import { useGetOptionSetsByNameQuery } from '@/app/services/lookups';
import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { OptionSet } from '@/rtk-query.gql';

import './HousingAutocomplete.css';

type HousingAutoCompleteProps = AutoCompleteProps & {
  className?: string;
  label?: string;
  defaultValue?: OptionSet;
  appendTo?: string | null;
};

const HOUSING_VALUE = 'Housing';

export const HousingAutoComplete = ({
  className,
  label,
  defaultValue,
  appendTo = 'self',
  ...autoCompleteProps
}: HousingAutoCompleteProps) => {
  const [selected, setSelected] = useState<OptionSet | undefined>();
  const [inputValue, setInputValue] = useState('');
  const [filtered, setFiltered] = useState<OptionSet[]>([]);

  const { data: HousingData } = useGetOptionSetsByNameQuery(HOUSING_VALUE);

  const search = useCallback(
    (event: { query: string }) => {
      const searchTerm = event.query.toLowerCase();
      const filteredHousing =
        HousingData?.filter(
          (Housing) =>
            Housing?.description?.toLowerCase().includes(searchTerm) ||
            Housing?.label?.toLowerCase().includes(searchTerm),
        ).slice(0, SELECTION_LIMIT) || [];

      setFiltered(filteredHousing);
    },
    [HousingData],
  );

  const itemTemplate = (Housing: OptionSet) => {
    return `${Housing.description} [${Housing.label}]`;
  };

  const handleSelect = (e: { value: OptionSet }) => {
    setSelected(e.value);
    if (setInputValue) {
      setInputValue(e.value?.description as string);
    }
  };

  const handleChange = (e: { value: string | OptionSet }) => {
    if (setInputValue) {
      const value =
        typeof e.value === 'object' && e.value !== null
          ? e.value.description
          : (e.value as string);
      setInputValue(value as string);
    }
  };

  return (
    <div
      className={`[ ivf-status-dropdown ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      {label && <label>{label}</label>}
      <AutoComplete
        appendTo={appendTo}
        suggestions={filtered}
        minLength={MIN_SEARCH_LENGTH}
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        itemTemplate={itemTemplate}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
        className={className}
      />
    </div>
  );
};
